import { Dimension, WidgetTypes } from "@superblocksteam/shared";

export { WidgetTypes };

export const WidgetTypesArr = Object.values(WidgetTypes);

export type WidgetType = keyof typeof WidgetTypes;

export type Hierarchy = {
  widgetId: string;
  widgetName: string;
  type: WidgetType;
  children?: Hierarchy[];
  icon?: React.ReactNode;

  top?: Dimension;
  left?: Dimension;
};

export const SLIDEOUT_DEFAULT_COLUMNS = Dimension.gridUnit(80);

export const GridDefaults = {
  DEFAULT_GRID_COLUMNS: 96,
  SLIDEOUT_DEFAULT_GRID_COLUMNS: SLIDEOUT_DEFAULT_COLUMNS.value,
  DEFAULT_GRID_ROW_HEIGHT: 12,
  DEFAULT_GRID_COLUMN_WIDTH: 12, // Note: we only use this for fixed pixel sizing - everything else will always have a fluid width value, stored in parentColumnSpace
  // Measured in rows
  CANVAS_EXTENSION_OFFSET: 0,

  // Measured in rows
  // This is the number of rows that the canvas will extend by when a widget is dragged to the bottom of the canvas
  // At this point, the canvas will wait EXTEND_CANVAS_TIMEOUT_MS before extending any more
  CANVAS_EXTENSION_INITIAL_LIMIT: 5,

  // Measured in rows
  FILL_PARENT_DEFAULT_MIN_HEIGHT: 1,
  // Measured in px
  FILL_PARENT_DEFAULT_MIN_WIDTH_PX: 12,

  EMPTY_CONTAINER_EDIT_MODE_HEIGHT: 6,
  EMPTY_CONTAINER_EDIT_MODE_WIDTH_PX: 120,
};

export enum CanvasLayout {
  FIXED = "FIXED_GRID",
  VSTACK = "VSTACK",
  HSTACK = "HSTACK",
}

export enum CanvasDistribution {
  TOP = "TOP",
  CENTER = "CENTER",
  BOTTOM = "BOTTOM",
  STRETCH = "STRETCH",
  SPACE_BETWEEN = "SPACE_BETWEEN",
  SPACE_AROUND = "SPACE_AROUND",
}

export enum CanvasAlignment {
  LEFT = "LEFT",
  CENTER = "CENTER",
  RIGHT = "RIGHT",
  STRETCH = "STRETCH",
  SPACE_BETWEEN = "SPACE_BETWEEN",
  SPACE_AROUND = "SPACE_AROUND",
}

export const PAGE_WIDGET_ID = "0";

// MODAL_TARGET_ID is the ID of the element that is targetted when modals
// are opened in the app.
export const SLIDEOUT_GAP = 20;

export enum SlideoutSize {
  EXTRA_SMALL = "EXTRA_SMALL",
  SMALL = "SMALL",
  MEDIUM = "MEDIUM",
  LARGE = "LARGE",
  FULLSCREEN = "FULLSCREEN",
}

export const SLIDEOUT_WIDTH_PRESETS: {
  EXTRA_SMALL: number;
  SMALL: number;
  MEDIUM: number;
  LARGE: number;
  FULLSCREEN: "100%";
} = {
  [SlideoutSize.EXTRA_SMALL]: 540,
  [SlideoutSize.SMALL]: 720,
  [SlideoutSize.MEDIUM]: 960,
  [SlideoutSize.LARGE]: 1200,
  [SlideoutSize.FULLSCREEN]: "100%",
};

export enum ModalSize {
  EXTRA_SMALL = "EXTRA_SMALL",
  SMALL = "SMALL",
  MEDIUM = "MEDIUM",
  LARGE = "LARGE",
  FULLSCREEN = "FULLSCREEN",
}

export const MODAL_PERIMETER_GAP = 16;

export const MODAL_WIDTH_PRESETS = {
  // in percentages
  [ModalSize.EXTRA_SMALL]: 30,
  [ModalSize.SMALL]: 40,
  [ModalSize.MEDIUM]: 50,
  [ModalSize.LARGE]: 75,
  [ModalSize.FULLSCREEN]: 100,
};

export interface WidgetCardProps {
  type: WidgetType;
  key: string;
  widgetCardName: string;
}

export type WidgetWidthModes = "px" | "gridUnit" | "fitContent" | "fillParent";
export type WidgetHeightModes = "px" | "gridUnit" | "fitContent" | "fillParent";

// When first dropping into a stack, if a widget is fitContent, we won't know it's fit-content pixel width
// without getting it from the DOM. So to avoid some re-render jank, we're using these hard coded values that
// we know are accurate.
// TODO: Find a better solution that doesn't need these values
